import * as Sentry from "@sentry/vue";
import Vue from "vue";
import VueTheMask from "vue-the-mask";
import VCurrencyField from "v-currency-field";
import { VTextField } from "vuetify/lib";
import moment from "moment-timezone";
import ConfirmDialog from "./components/ConfirmDialog";
import { APP_VERSION, IS_DEV_ENV, IS_CYPRESS_ENV, SENTRY_DNS, GA_MEASUREMENT_ID } from "./utils/constants";
import { supabase } from "./services/SupabaseService";
import { getBrowserLanguage, getUserFromLocalStorage } from "./utils/helpers";
import { getCurrentTimezone } from "./utils/date";
import { SUPPORTED_LOCALES } from "./types";
import { SET_APP_LOCALE, SET_USER } from "./store/action-types";
import App from "./App.vue";
import router from "./router";
import store from "./store/store";
import i18n from "./i18n";
import vuetify from "./plugins/vuetify";
import User from "./entities/User";

import "@mdi/font/css/materialdesignicons.min.css";
import "@fontsource/open-sans";
import "@fontsource/space-grotesk";

import "flag-icons/css/flag-icons.min.css";

// Define the default timezone for the Moment library
moment.tz.setDefault(getCurrentTimezone());

Vue.component("fatal-error", () => import("./components/FatalError.vue"));
Vue.component("v-text-field", VTextField); // required because of "v-currency-field" component
Vue.component("app-settings", () => import("./components/AppSettings.vue"));
Vue.component("a-button", () => import("./components/AButton.vue"));
Vue.component("icon-logo", () => import("./components/IconLogo.vue"));
Vue.component("text-field", () => import("./components/TextField.vue"));
Vue.component("fullscreen-loader", () => import("./components/FullscreenLoader.vue"));

// load previous app state
(() => {
  // load the locale from the local storage or the browser language
  const locale = localStorage.getItem("parlaeditor_app_locale") || getBrowserLanguage();
  if (locale && locale in SUPPORTED_LOCALES) {
    store.dispatch(`app/${SET_APP_LOCALE}`, locale);
  }
})();

(() => {
  // load the signed in user from the local storage
  const storedUser = getUserFromLocalStorage();
  if (storedUser) {
    store.dispatch(`auth/${SET_USER}`, User.fromJSON(storedUser));
  }

  // bind listener to sync the store whenever the current user changes (sign in/out)
  supabase.auth.onAuthStateChange(async (event, session) => {
    if (["SIGNED_IN", "USER_UPDATED", "TOKEN_REFRESHED"].includes(event) && session?.user) {
      const user = User.fromSupabaseUser(session.user);
      store.dispatch(`auth/${SET_USER}`, user);
      Sentry.setUser({ id: user.id as string });
    } else if (event === "SIGNED_OUT") {
      store.dispatch(`auth/${SET_USER}`, null);
      Sentry.setUser(null);
    }
  });
})();

Vue.config.productionTip = true;

(() => {
  let sentryEnv = "prod";

  if (IS_DEV_ENV) {
    sentryEnv = "dev";
  } else if (IS_CYPRESS_ENV) {
    sentryEnv = "cypress";
  }

  // don't init Sentry in dev or cypress environments because they take up too much "Replay" quota
  const enableSentry = !IS_DEV_ENV && !IS_CYPRESS_ENV;

  Sentry.init({
    Vue,
    dsn: enableSentry ? SENTRY_DNS : undefined,
    release: `app@${APP_VERSION}`,
    environment: sentryEnv,
    integrations: [],
    replaysOnErrorSampleRate: 0,
    replaysSessionSampleRate: 0,
    tracesSampleRate: 0,
    maxValueLength: 500, // Sentry truncates error messages longer than maxValueLength; it's 250 by default
  });
})();

(() => {
  if (IS_DEV_ENV || IS_CYPRESS_ENV) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  const inlineScript = document.createElement("script");
  inlineScript.text = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${GA_MEASUREMENT_ID}');
  `;
  document.head.appendChild(inlineScript);
})();

Vue.use(VueTheMask);
Vue.use(VCurrencyField);
Vue.use(ConfirmDialog, { vuetify });

new Vue({
  router,
  store,
  vuetify,
  i18n,
  render: (h) => h(App),
}).$mount("#app");
